import { useRef } from "react";
import { AnimatePresence, motion, type Transition } from "motion/react";
import { Drawer, DrawerContent } from "../../../shadcn/ui/drawer";
import { X } from "lucide-react";

import { AccordionSectionId } from "../types";
import { MobileFooterConfig } from "../scheduler/SmartScheduler";
import { MobileStepperFlow } from "./MobileStepperFlow";
import { slideVariants, SLIDE_TRANSITION } from "../../../animations/slide";

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  activeSection: AccordionSectionId | null;
  sectionOrder: AccordionSectionId[];
  completedSections: Set<AccordionSectionId>;
  channelPreferenceSkipped: boolean;
  onSectionChange: (sectionId: AccordionSectionId) => void;
  renderSectionContent: (sectionId: AccordionSectionId) => React.ReactNode;
  mobileConfirmConfig: {
    onClick: () => void;
    disabled: boolean;
    disabledMessage?: string;
  } | null;
  reviewFooterConfig: MobileFooterConfig | null;
  /** When provided, replaces MobileStepperFlow content (e.g. inline credit view) */
  inlineOverrideContent?: React.ReactNode;
}

export function MobileDrawer({
  isOpen,
  onClose,
  activeSection,
  sectionOrder,
  completedSections,
  channelPreferenceSkipped,
  onSectionChange,
  renderSectionContent,
  mobileConfirmConfig,
  reviewFooterConfig,
  inlineOverrideContent,
}: MobileDrawerProps) {
  const showCredit = !!inlineOverrideContent;
  const directionRef = useRef(1);
  const prevShowCreditRef = useRef(showCredit);

  if (prevShowCreditRef.current !== showCredit) {
    directionRef.current = showCredit ? 1 : -1;
    prevShowCreditRef.current = showCredit;
  }

  const prefersReducedMotion =
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  const effectiveTransition: Transition = prefersReducedMotion
    ? { duration: 0 }
    : SLIDE_TRANSITION;

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DrawerContent className="flex h-[100dvh] flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <h1 className="text-xl font-bold text-foreground">Messages</h1>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex items-center justify-center h-11 w-11 -mr-2"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <AnimatePresence
          mode="wait"
          custom={directionRef.current}
          initial={false}
        >
          <motion.div
            key={showCredit ? "credit" : "stepper"}
            custom={directionRef.current}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={effectiveTransition}
            className="flex flex-1 min-h-0 flex-col"
          >
            {inlineOverrideContent ?? (
              <MobileStepperFlow
                activeSection={activeSection}
                sectionOrder={sectionOrder}
                completedSections={completedSections}
                channelPreferenceSkipped={channelPreferenceSkipped}
                onSectionChange={onSectionChange}
                renderSectionContent={renderSectionContent}
                mobileConfirmConfig={mobileConfirmConfig}
                reviewFooterConfig={reviewFooterConfig}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </DrawerContent>
    </Drawer>
  );
}
